/* eslint-disable jsx-a11y/label-has-associated-control */

import { FC, useState } from 'react';
import { TextInput, Title, Text, Flex } from '@tremor/react';
import { acceptInvitationCloudFunction, auth } from '@/firebase/init';
import { useRevalidator } from 'react-router-dom';
import { UserInvitation } from '@components/layouts/Modals/UserProfileModal/UserInvitation';
import { EntityType } from '@/types/Organization';
import { GeneralModalProps } from '@/types/modals';
import { ModalWrapper } from '../ModalWrapper/ModalWrapper';
import { sleep } from '@/utils/helpers';
import { OutlinedButton } from '../../Buttons/OutlinedButton/OutlinedButton';
import { UserService } from '@/services/userService';
import { showToast } from '@/utils/toasts';
import { useUserOrganizationsInvitations } from '@/services/queries/useUserOrganizationInvitations';
import { useUserWorkspacesInvitations } from '@/services/queries/useUserWorkspacesInvitations';
import { useUserPermissionsStore } from '@/stores/userPermissionsStore';
import { assert } from '@/utils/asserts';

type Props = GeneralModalProps;

export const UserProfileModal: FC<Props> = ({ isOpen, setClose, ...rest }) => {
  return (
    <ModalWrapper className="w-1/3 h-fit" isOpen={isOpen} onRequestClose={setClose}>
      <UserProfileModalContent setClose={setClose} {...rest} />
    </ModalWrapper>
  );
};

type ContentProps = Pick<Props, 'setClose'>;

export const UserProfileModalContent: FC<ContentProps> = ({ setClose }) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const revalidator = useRevalidator();
  const userId = useUserPermissionsStore((state) => state.userId);
  assert(typeof userId === 'string', 'userId not defined');

  const organizationInvitations = useUserOrganizationsInvitations(userId);
  const invitations = useUserWorkspacesInvitations(userId);

  const handleDeleteAccount = async () => {
    setIsLoading(true);
    try {
      await UserService.deleteUser();
      // Don't know why, but it takes firebase about a second to update records
      await sleep(1000);
      revalidator.revalidate();
    } catch (e) {
      console.error(e);
      showToast('Error!', 'Failed to delete user account. Try again later!', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAcceptInvitation = async (
    workspaceId: string,
    invitationId: string,
    userId: string,
    entityType: EntityType,
  ) => {
    try {
      await acceptInvitationCloudFunction({
        entityId: workspaceId,
        invitationId,
        entityType,
      });
    } catch (error) {
      console.error(error);
      showToast('Error!', 'Failed to accept invitation. Try again later!', 'error');
    }
  };

  const handleDeleteInvitation = async (
    workspaceId: string,
    invitationId: string,
    entityType: EntityType,
  ) => {
    try {
      await UserService.deleteInvitation(workspaceId, invitationId, entityType);
    } catch (error) {
      showToast('Error!', 'Failed to delete invitation. Try again later!', 'error');
    }
  };

  const isDeletingConfirmation = (
    <div className="border-2 border-red-600 p-4 rounded-xl">
      <Text className="text-red-600 font-bold">
        Are you sure you want to delete your account? This action is irreversible.
      </Text>
      <Flex className="justify-start gap-4 mt-4">
        <OutlinedButton
          isLoading={isLoading}
          className="bg-red-600 border-red-600 hover:border-red-600 hover:bg-red-600 text-white"
          onClick={handleDeleteAccount}
          spinnerSize="20px"
        >
          Confirm Deletion
        </OutlinedButton>
        <OutlinedButton disabled={isLoading} onClick={() => setIsDeleting(false)}>
          Cancel
        </OutlinedButton>
      </Flex>
    </div>
  );

  return (
    <>
      <Title className="font-ubuntu font-bold text-2xl text-[#425565]">
        Your account
      </Title>
      <form className="w-full">
        <label className="w-full" htmlFor="user-profile-email">
          <TextInput
            placeholder="Your Email"
            disabled
            className="flex items-center py-2 pr-4 !w-full h-11 bg-white border-2 border-[#9E9E9E] rounded-xl [&>input]:w-full"
            id="user-profile-email"
            defaultValue={auth.currentUser?.email || ''}
          />
        </label>
      </form>
      {!isDeleting && (
        <OutlinedButton
          disabled={isLoading}
          className="border-red-600 text-red-600 hover:border-red-600"
          onClick={() => setIsDeleting(true)}
          spinnerSize="20px"
          spinnerColor="#BA2634"
        >
          Delete Account
        </OutlinedButton>
      )}
      {isDeleting && isDeletingConfirmation}
      <section className="w-full">
        <Text className="font-ubuntu font-bold text-lg text-[#425565]">
          Workspaces Invitations
        </Text>
        {invitations.length === 0 ? (
          <Text>No pending invitations found</Text>
        ) : (
          <>
            <Text className="font-ubuntu font-bold text-lg text-[#425565]">
              Workspace name
            </Text>
            {invitations.map((invitation) => (
              <UserInvitation
                entityType="workspaces"
                workspaceId={invitation.workspaceId}
                onDeleteInvitation={handleDeleteInvitation}
                onAcceptInvitation={handleAcceptInvitation}
                key={invitation.id}
                invitationId={invitation.id}
                workspaceName={invitation.workspaceName}
              />
            ))}
          </>
        )}
      </section>
      <section className="w-full">
        <Text className="font-ubuntu font-bold text-lg text-[#425565]">
          Organizations Invitations
        </Text>
        {organizationInvitations.length === 0 ? (
          <Text>No pending invitations found</Text>
        ) : (
          <>
            <Text className="font-ubuntu font-bold text-lg text-[#425565]">
              Organization name
            </Text>
            {organizationInvitations.map((invitation) => (
              <UserInvitation
                entityType="organizations"
                workspaceId={invitation.organizationId}
                onDeleteInvitation={handleDeleteInvitation}
                onAcceptInvitation={handleAcceptInvitation}
                key={invitation.id}
                invitationId={invitation.id}
                workspaceName={invitation.organizationName}
              />
            ))}
          </>
        )}
      </section>
      <OutlinedButton
        disabled={isLoading}
        onClick={setClose}
        className="h-14 w-full text-white bg-primary-default rounded-xl hover:bg-primary-600 hover:border-primary-600 transition-colors duration-200"
      >
        Close
      </OutlinedButton>
    </>
  );
};
